/**
 * Goals Block Component
 * 
 * Shows referral goals with progress bars and claim buttons.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Target, Trophy, ChevronDown, ChevronUp, Loader2, Check, Gift } from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Progress } from '../ui/progress'
import { apiClient } from '../../lib/api/client'
import { toast } from '../../hooks/use-toast'
import { cn } from '../../lib/utils'
import type { ReferralGoal } from '../../types'

interface GoalsBlockProps {
  goals: ReferralGoal[]
  activeReferrals: number
  onGoalClaimed: () => void
}

export function GoalsBlock({ goals, activeReferrals, onGoalClaimed }: GoalsBlockProps) {
  const { t } = useTranslation('common')
  const [showCompleted, setShowCompleted] = useState(false)
  const [claiming, setClaiming] = useState<number | null>(null)

  const activeGoals = goals.filter(g => g.status !== 'claimed')
  const completedGoals = goals.filter(g => g.status === 'claimed')

  const handleClaim = async (goalId: number) => {
    try {
      setClaiming(goalId)
      const result = await apiClient.referrals.claimGoalReward(goalId)
      
      if (result.success) {
        toast({
          title: t('referrals.goalClaimed', 'Goal reward claimed!'),
          description: result.message,
        })
        onGoalClaimed()
      } else {
        toast({
          title: t('referrals.claimFailed', 'Claim failed'),
          description: result.message,
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: t('referrals.claimFailed', 'Claim failed'),
        description: error.message || t('errors.generic', 'Something went wrong'),
        variant: 'destructive',
      })
    } finally {
      setClaiming(null)
    }
  }

  const getRewardText = (goal: ReferralGoal) => {
    if (goal.reward_type === 'credits') {
      return `$${goal.reward_amount.toFixed(0)} ${t('referrals.credits', 'credits')}`
    } else if (goal.reward_type === 'bonus_downloads') {
      return `${goal.reward_amount} ${t('referrals.downloads', 'downloads')}`
    }
    return `${goal.reward_amount}`
  }

  return (
    <Card className="goals-block">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-emerald-500" />
          {t('referrals.goalsProgress', 'Goals & Progress')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Active goals */}
        {activeGoals.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {t('referrals.allGoalsCompleted', 'You have completed all available goals! Check back later for new ones.')}
          </p>
        ) : (
          <div className="active-goals space-y-4">
            {activeGoals.map(goal => (
              <div 
                key={goal.id}
                className={cn(
                  "goal-card p-4 rounded-lg border transition-all",
                  goal.status === 'completed' 
                    ? "bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30" 
                    : "bg-card border-border"
                )}
              >
                <div className="goal-header flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      {goal.status === 'completed' && (
                        <Trophy className="h-4 w-4 text-amber-500" />
                      )}
                      {goal.name}
                    </h4>
                    <p className="text-sm text-muted-foreground">{goal.description}</p>
                  </div>
                  <div className="reward-badge px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-sm font-medium whitespace-nowrap">
                    <Gift className="h-3 w-3 inline mr-1" />
                    {getRewardText(goal)}
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="progress-section mb-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">
                      {t('referrals.progress', 'Progress')}
                    </span>
                    <span className="font-medium">
                      {goal.current_count} / {goal.required_count} {t('referrals.activeFriends', 'active friends')}
                    </span>
                  </div>
                  <Progress value={goal.progress_percent} className="h-2" />
                </div>

                {/* Claim button for completed goals */}
                {goal.status === 'completed' && (
                  <Button
                    onClick={() => handleClaim(goal.id)}
                    disabled={claiming !== null}
                    className="claim-btn w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                  >
                    {claiming === goal.id ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Gift className="h-4 w-4 mr-2" />
                    )}
                    {t('referrals.claimReward', 'Claim Reward')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Completed goals (collapsible) */}
        {completedGoals.length > 0 && (
          <div className="completed-goals pt-4 border-t border-border">
            <button
              onClick={() => setShowCompleted(!showCompleted)}
              className="toggle-completed w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <span className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" />
                {t('referrals.completedGoals', 'Completed goals')} ({completedGoals.length})
              </span>
              {showCompleted ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
            
            {showCompleted && (
              <div className="completed-list mt-3 space-y-2">
                {completedGoals.map(goal => (
                  <div 
                    key={goal.id}
                    className="completed-goal flex items-center justify-between p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20"
                  >
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-amber-500" />
                      <span className="text-sm font-medium">{goal.name}</span>
                    </div>
                    <span className="text-xs text-emerald-600">
                      +{getRewardText(goal)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Summary line */}
        <div className="summary-line text-center text-xs text-muted-foreground pt-2">
          {t('referrals.youHaveInvited', 'You have invited {{count}} friends', { count: activeReferrals })}
        </div>
      </CardContent>
    </Card>
  )
}

